import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, cn } from '@mf-tools/domains-ui';
import { ArrowRightLeft, Shield, Users, Wallet } from 'lucide-react';
import { Container } from '../components';

export interface SolutionProps {
  className?: string;
}

export function Solution({ className }: SolutionProps): React.ReactElement {
  return (
    <section id="why" className={cn('py-14 sm:py-20', className)} aria-labelledby="solution-title">
      <Container>
        <div className="space-y-10">
          <div className="max-w-3xl space-y-4">
            <h2 id="solution-title" className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              OpsPilott replaces fragmented ops with one operating system.
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
              <span className="font-medium text-foreground">Others sell tools. OpsPilott delivers outcomes.</span> You
              get a unified platform that standardizes workflows across hiring, finance, and security — with visibility
              and approvals built in.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
              <span className="font-medium text-foreground">We replace 5–10 vendors with one unified platform.</span>
            </p>
          </div>

          <div className="grid gap-4 lg:grid-cols-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4 text-primary" />
                  HR / People
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Hiring, onboarding, policies, and readiness — with owners and approvals.
              </CardContent>
            </Card>
            <Card className="hidden items-center justify-center lg:flex">
              <CardContent className="p-6">
                <ArrowRightLeft className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Wallet className="h-4 w-4 text-primary" />
                  Finance
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Spend, payments, reporting readiness, and cash visibility — without spreadsheets.
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Shield className="h-4 w-4 text-primary" />
                  Security / Compliance
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Policies, controls, and evidence workflows that keep you enterprise-ready.
              </CardContent>
            </Card>
          </div>

          <Card className="border-primary/25 bg-primary/5">
            <CardContent className="p-6 sm:p-8">
              <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
                <div>
                  <div className="text-base font-semibold text-foreground">Unified in OpsPilott</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    One set of workflows, one audit trail, one source of truth.
                  </div>
                </div>
                <div className="inline-flex rounded-full border bg-background px-3 py-1 text-xs text-muted-foreground">
                  Outcome-driven ops
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Container>
    </section>
  );
}

